import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Phone, Menu, X } from "lucide-react";
import { getMeniny, formatSkDate } from "../data/namedays";
import { useLang } from "../i18n";

const LOCALES = { sk: "sk-SK", en: "en-GB", de: "de-DE" };

export const scrollToId = (hash) => {
  if (!document.querySelector(hash)) {
    if (hash === "#top") window.scrollTo({ top: 0, behavior: "smooth" });
    return;
  }
  if (window.__lenis) {
    window.__lenis.scrollTo(hash, { offset: -72 });
  } else {
    document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
  }
};

const LangSwitch = ({ onPick }) => {
  const { lang, setLang } = useLang();
  return (
    <div className="flex items-center gap-1" data-testid="lang-switcher">
      {["sk", "en", "de"].map((l) => (
        <button key={l} data-testid={`lang-switch-${l}`}
          onClick={() => { setLang(l); onPick?.(); }}
          className={`font-mono text-xs px-2 py-1 border transition-colors duration-300 ${lang === l ? "border-safety text-safety" : "border-transparent text-slate-500 hover:text-slate-200"}`}>
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
};

export const Navbar = () => {
  const { lang, t } = useLang();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [meniny] = useState(() => getMeniny());
  const dateStr = formatSkDate(new Date(), LOCALES[lang]);

  const LINKS = [
    { href: "#priestory", label: t("nav_spaces"), id: "nav-link-spaces" },
    { href: "#sluzby", label: t("nav_services"), id: "nav-link-services" },
    { href: "/galeria", label: t("nav_gallery"), id: "nav-link-gallery", route: true },
    { href: "#kalkulacka", label: t("nav_calc"), id: "nav-link-calculator" },
    { href: "#kontakt", label: t("nav_contact"), id: "nav-link-contact" },
  ];

  const go = (href) => {
    setOpen(false);
    if (href.startsWith("/")) {
      if (pathname !== href) { navigate(href); window.scrollTo(0, 0); }
      return;
    }
    if (pathname !== "/") navigate("/" + href);
    else scrollToId(href);
  };

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <header data-testid="navbar" className="fixed top-0 inset-x-0 z-50 border-b border-steelborder bg-ink/85 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between gap-4">
        <a href="/" onClick={(e) => { e.preventDefault(); go("#top"); }}
          data-testid="brand-link" className="flex items-center group">
          <img src="/brand/logo-dark.png" alt="ZTS F-Invest s.r.o." data-testid="brand-logo"
            className="h-11 w-auto transition-transform duration-300 group-hover:scale-[1.03]" />
        </a>

        <div data-testid="meniny-widget" className="hidden md:flex items-center gap-2 border border-steelborder bg-gunmetal px-3 py-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-safety animate-pulse-dot" />
          <span className="font-mono text-xs text-slate-400">
            {t("today")} <span className="text-slate-200">{dateStr}</span>
            {meniny && (
              <> · {t("nameday")} <span className="text-safety font-semibold">{meniny}</span></>
            )}
          </span>
        </div>

        <nav className="hidden lg:flex items-center gap-6">
          {LINKS.map((l) => (
            <a key={l.href} href={l.href} data-testid={l.id} aria-current={l.route && pathname === l.href ? "page" : undefined}
              onClick={(e) => { e.preventDefault(); go(l.href); }}
              className={`font-mono text-xs tracking-[0.2em] uppercase hover:text-safety transition-colors duration-300 ${l.route && pathname === l.href ? "text-safety" : "text-slate-400"}`}>
              {l.label}
            </a>
          ))}
          <LangSwitch />
          <a href="tel:+421905207101" data-testid="nav-phone-link"
            className="flex items-center gap-2 whitespace-nowrap bg-safety text-ink font-heading font-bold uppercase tracking-wide px-4 py-2 text-sm hover:bg-safety-hover transition-colors duration-300">
            <Phone className="w-4 h-4" /> 0905 207 101
          </a>
        </nav>

        <button data-testid="mobile-menu-button" onClick={() => setOpen(!open)}
          className="lg:hidden text-slate-200 p-2" aria-label="Menu">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-steelborder bg-ink px-4 py-6 space-y-4">
          {LINKS.map((l) => (
            <a key={l.href} href={l.href} data-testid={`mobile-${l.id}`}
              onClick={(e) => { e.preventDefault(); go(l.href); }}
              className="block font-heading text-2xl font-bold uppercase text-slate-200 hover:text-safety transition-colors">
              {l.label}
            </a>
          ))}
          <LangSwitch onPick={() => setOpen(false)} />
          <p className="font-mono text-xs text-slate-400 pt-2">
            {dateStr}{meniny && <> · {t("nameday")} <span className="text-safety">{meniny}</span></>}
          </p>
        </div>
      )}
    </header>
  );
};
